import { useEffect, useMemo, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import { IGetRowsParams, IDatasource } from "ag-grid-community";
import {
  ConsultantsQueryInput,
  useGetConsultantsLazyQuery,
  useGetConsultantsTotalLazyQuery
} from "@bonliva-crm/graphql";
import { ConsultantTableColumns } from "@bonliva-crm/shared";
import { useDebouncedValue } from "@hooks";
import { useConsultantSearchFilters } from "../../hooks/use-consultant-search-filters";

type Props = {
  getGridRef: () => AgGridReact;
};

export function useConsultantTableDatasource(props: Props) {
  const { getGridRef } = props;

  const {
    queryInput,
    filterManager: {
      formMethods: { watch }
    }
  } = useConsultantSearchFilters();

  const debouncedInput = useDebouncedValue(queryInput, 400);

  const queryInputRef = useRef<ConsultantsQueryInput>();

  const [GetConsultants] = useGetConsultantsLazyQuery();
  const [getConsultantsTotal] = useGetConsultantsTotalLazyQuery();

  const totalCached = useRef<{ input: ConsultantsQueryInput; result: number } | null>(
    null
  );

  const sort = watch("sort");

  useEffect(() => {
    queryInputRef.current = queryInput;
  }, [queryInput]);

  const getTotalCached = async (filter: ConsultantsQueryInput) => {
    let result = 0;

    if (totalCached.current && totalCached.current.input === filter) {
      result = totalCached.current.result;
    } else {
      const queryResult = await getConsultantsTotal({
        variables: { filter },
        fetchPolicy: "no-cache"
      });

      result = queryResult.data.consultantsTotal.total;
    }

    totalCached.current = { input: filter, result };

    return result;
  };

  const getRows = (rowParams: IGetRowsParams) => {
    const filter = queryInputRef.current;

    const withCallListStats = !!getGridRef()
      ?.api.getColumn(ConsultantTableColumns.CallListStats)
      ?.isVisible();

    const limit = rowParams.endRow - rowParams.startRow;
    const skip = rowParams.startRow;

    Promise.all([
      GetConsultants({
        variables: {
          limit,
          skip,
          filter,
          withEvents: Boolean(filter.fullTextSearch) && Boolean(filter.text),
          filterText: filter.text || "",
          withCallListStats,
          sortOrder: sort.sortOrder,
          sortBy: sort.sortBy
        },
        fetchPolicy: "no-cache"
      }),
      getTotalCached(filter)
    ])
      .then((res) => {
        rowParams.successCallback(res[0].data.consultants.items, res[1]);
      })
      .catch((err) => {
        console.error(err);
        rowParams.failCallback();
      });
  };

  const dataSource: IDatasource = useMemo(() => {
    return {
      rowCount: undefined,
      getRows,
      destroy: () => {
        const grid = getGridRef();
        if (!grid?.api?.isDestroyed()) {
          grid?.api.getSelectedNodes()?.forEach((node) => {
            node.setSelected(false);
          });
        }
      }
    };
  }, [debouncedInput]);

  return dataSource;
}
